import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  ExaminationResult,
  ImageWithDescription,
  RoomExaminationResult,
  SelfCreatedRoom,
  TooFarReason,
} from '../../../examinationResult.model';
import { environment } from '../../../../environments/environment';
import {
  ClosedRoomExaminationResultDto,
  ExaminationResultDto,
  ImageWithDescriptionDto,
  RoomExaminationResultDto,
  RoomExaminationResultInfoDto,
  SelfCreatedRoomDto,
  TariffDto,
  TvQuantityDto,
  TvSizeDto,
  TvTypeDto,
} from '../../../lead.model';
import { BaseHttpService } from '../../../services/base-http.service';
import { GfsUploadService } from '../../../services/gfs-upload/gfs-upload.service';
import { forkJoin, Observable, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ExaminationResultService extends BaseHttpService {
  protected override maxRetries = 8;
  protected override retryBaseDelay = 3000;

  constructor(
    private readonly http: HttpClient,
    private readonly gfsUploadService: GfsUploadService,
  ) {
    super();
  }

  private calculateImagesWithDescriptionDto(
    imageWithDescriptions: ImageWithDescription[],
    roomExaminationResultId: string,
    examinationId: string,
    uploadTasks: Observable<unknown>[],
  ): ImageWithDescriptionDto[] {
    const roomImages: ImageWithDescriptionDto[] = [];
    let id = 0;
    for (const imageWithDescription of imageWithDescriptions) {
      const blob: Blob = this.base64ToBlob(imageWithDescription.image);
      const filename = id.toString() + '.jpeg';

      // Add upload task to the array
      uploadTasks.push(
        this.gfsUploadService.uploadFile(
          new File([blob], filename, { type: 'image/jpeg' }),
          examinationId,
          roomExaminationResultId,
        ),
      );

      roomImages.push({
        filename: filename,
        description: imageWithDescription.description,
      });
      id = id + 1;
    }
    return roomImages;
  }

  private convertClosedRoomExaminationResultToDto(
    roomExaminationResult: RoomExaminationResult,
    examinationId: string,
    uploadTasks: Observable<unknown>[],
  ): ClosedRoomExaminationResultDto {
    if (
      !roomExaminationResult.info.roomIsClosed ||
      roomExaminationResult.info.roomIsClosedReason === undefined
    ) {
      throw new Error(
        'cannot call method convertClosedRoomExaminationResultToDto() when room is not closed',
      );
    }
    const roomImages = this.calculateImagesWithDescriptionDto(
      roomExaminationResult.info.images,
      roomExaminationResult.id,
      examinationId,
      uploadTasks,
    );

    return {
      roomClosedReason: roomExaminationResult.info.roomIsClosedReason,
      id: roomExaminationResult.id,
      imageWithDescriptionDtos: roomImages,
      notes: roomExaminationResult.info.roomNotes,
    };
  }

  private convertRoomExaminationResultToDto(
    roomExaminationResult: RoomExaminationResult,
    examinationId: string,
    uploadTasks: Observable<unknown>[],
  ): RoomExaminationResultDto {
    if (roomExaminationResult.info.roomIsClosed) {
      throw new Error(
        'cannot call method convertClosedRoomExaminationResultToDto() when room is not closed',
      );
    }
    const roomImages = this.calculateImagesWithDescriptionDto(
      roomExaminationResult.info.images,
      roomExaminationResult.id,
      examinationId,
      uploadTasks,
    );

    const tariffs = roomExaminationResult.info.tariffs;
    const tariffsDto: TariffDto = {};
    if (tariffs.audio) {
      tariffsDto.audio = 'AUDIO';
    }
    // FR-15
    if (tariffs.radio) {
      switch (tariffs.radioType) {
        case 'public':
          tariffsDto.radio = 'RADIO_PUBLIC';
          break;
        case 'private':
          tariffsDto.radio = 'RADIO_PRIVATE';
          break;
        case 'not-determinable':
          tariffsDto.radio = 'RADIO_NOT_DETERMINABLE';
          break;
        default:
          throw new Error('trying to submit an illegal radio tariff');
      }
      tariffsDto.radioStation = tariffs.radioStation ?? undefined;
    }
    if (tariffs.audioOrRadio) {
      tariffsDto.audioOrRadio = 'AUDIO_OR_RADIO';
    }
    if (tariffs.video) {
      if (tariffs.videoSize == 'below-42-inches') {
        tariffsDto.video = 'BELOW_42_INCHES';
      } else if (tariffs.videoSize == 'above-42-inches') {
        tariffsDto.video = 'ABOVE_42_INCHES';
      } else {
        throw new Error('not supported video tariff: ' + tariffs.videoSize);
      }
    }
    if (tariffs.tv) {
      let size: TvSizeDto | undefined = undefined;
      let type: TvTypeDto | undefined = undefined;
      let quantity: TvQuantityDto | null;
      if (tariffs.tvSize == 'below-65-inches') {
        size = 'BELOW_65_INCHES';
      }
      if (tariffs.tvSize == 'above-65-inches') {
        size = 'ABOVE_65_INCHES';
      }
      switch (tariffs.tvType) {
        case 'public':
          type = 'TV_PUBLIC';
          break;
        case 'private':
          type = 'TV_PRIVATE';
          break;
        case 'not-determinable':
          type = 'TV_NOT_DETERMINABLE';
          break;
        default:
          throw new Error('trying to submit an illegal tv tariff');
      }
      if (tariffs.tvQuantity) {
        quantity = tariffs.tvQuantity;
      } else {
        quantity = null;
      }
      tariffsDto.tv = { tvType: type!, tvSize: size!, tvQuantity: quantity!, tvStation: tariffs.tvStation ?? undefined };
    }

    const roomExaminationResultInfo: RoomExaminationResultInfoDto = {
      roomDetailInformationExaminationResultDTO: {
        name: roomExaminationResult.info.name,
        size: roomExaminationResult.info.size,
        usageSpaceCategory: roomExaminationResult.info.usageSpaceCategory,
      },
      notes: roomExaminationResult.info.roomNotes,
      imageWithDescriptionDtos: roomImages,
      tariffs: tariffsDto,
    };

    return { id: roomExaminationResult.id, info: roomExaminationResultInfo };
  }

  submitToBackend(
    draft: ExaminationResult,
    latitude: number,
    longitude: number,
    reason: TooFarReason | undefined,
  ) {
    // Array to collect all image upload tasks
    const uploadTasks: Observable<unknown>[] = [];

    const roomExaminations: RoomExaminationResultDto[] = [];
    const closedRoomExaminations: ClosedRoomExaminationResultDto[] = [];
    for (const roomExamination of draft.roomExaminations) {
      const selfCreatedRoomIds = draft.selfCreatedRooms.map(
        (selfCreatedRoom: SelfCreatedRoom) => selfCreatedRoom.selfAssignedId,
      );
      if (!selfCreatedRoomIds.includes(roomExamination.id)) {
        if (roomExamination.info.roomIsClosed) {
          const closedRoomExamination =
            this.convertClosedRoomExaminationResultToDto(
              roomExamination,
              draft.id,
              uploadTasks,
            );
          closedRoomExaminations.push(closedRoomExamination);
        } else {
          const roomExaminationResultDto: RoomExaminationResultDto =
            this.convertRoomExaminationResultToDto(
              roomExamination,
              draft.id,
              uploadTasks,
            );
          roomExaminations.push(roomExaminationResultDto);
        }
      }
    }

    const selfCreatedRoomsDto: SelfCreatedRoomDto[] = [];
    for (const selfCreatedRoom of draft.selfCreatedRooms) {
      const roomExamination = draft.roomExaminations.find(
        (roomExamination: RoomExaminationResult) =>
          roomExamination.id == selfCreatedRoom.selfAssignedId,
      );
      let roomExaminationResultInfoDto:
        | RoomExaminationResultInfoDto
        | undefined = undefined;
      if (roomExamination !== undefined) {
        if (roomExamination.info.roomIsClosed) {
          throw new Error('Cannot close a self created room');
        }
        const roomExaminationResultDto: RoomExaminationResultDto =
          this.convertRoomExaminationResultToDto(
            roomExamination,
            draft.id,
            uploadTasks,
          );
        roomExaminationResultInfoDto = roomExaminationResultDto.info;
      }
      const selfCreatedRoomDto = {
        selfAssignedId: selfCreatedRoom.selfAssignedId,
        info: selfCreatedRoom.info,
        roomExaminationResultInfo: roomExaminationResultInfoDto,
      } as SelfCreatedRoomDto;
      selfCreatedRoomsDto.push(selfCreatedRoomDto);
    }

    // Upload general images to GFS
    const generalImages: ImageWithDescriptionDto[] = [];
    let id = 0;
    for (const imageWithDescription of draft.images) {
      const blob: Blob = this.base64ToBlob(imageWithDescription.image);
      const filename = id.toString() + '.jpeg';

      // Add upload task
      uploadTasks.push(
        this.gfsUploadService.uploadFile(
          new File([blob], filename, { type: 'image/jpeg' }),
          draft.id
        ),
      );

      generalImages.push({
        filename: filename,
        description: imageWithDescription.description,
      });
      id = id + 1;
    }

    const examinationResultDto: ExaminationResultDto = {
      id: draft.id,
      startLogDTO: {
        timeIso8601: draft.startTimeIso8601,
        coordinatesDTO: {
          latitude: draft.startCoordinates.latitude,
          longitude: draft.startCoordinates.longitude,
        },
        reason: draft.startReason,
      },
      endLogDTO: {
        timeIso8601: new Date().toISOString(),
        coordinatesDTO: {
          latitude: latitude,
          longitude: longitude,
        },
        reason: reason,
      },
      name: draft.name,
      operatorName: draft.operatorName,
      leadCategory: draft.leadCategory,
      tel: draft.tel,
      website: draft.website,
      openingTimes: draft.openingTimes,
      notes: draft.notes,
      images: generalImages,
      roomExaminations: roomExaminations,
      selfCreatedRooms: selfCreatedRoomsDto,
      closedRoomExaminations: closedRoomExaminations,
      notPossibleReason: draft.notPossibleReason,
      playbackDevicesVisible: draft.playbackDevicesVisible,
      playbackDevicesLocation: draft.playbackDevicesLocation ?? undefined,
      guestsPresent: draft.guestsPresent,
      guestCount: draft.guestCount ?? undefined,
      submissionId: draft.submissionId,
    };

    // Upload all images first (in parallel), then submit the JSON DTO
    const uploadObservable: Observable<unknown> = uploadTasks.length > 0 ? forkJoin(uploadTasks) : of(null);

    return uploadObservable.pipe(
      switchMap(() => {
        // After all images are uploaded, send the JSON DTO
        return this.applyHttpOperators(
          this.http.post<string>(
            environment.backend_host + '/examination-result',
            examinationResultDto,
          ),
        );
      }),
    );
  }

  public retryUploadToSap(examinationResultId: string) {
    return this.http.post<string>(
      environment.backend_host + '/examination-result/retry-sap-upload/' + examinationResultId,
      null,
    )
  }

  private base64ToBlob(base64: string): Blob {
    const jpegPrefix = 'data:image/jpeg;base64,';
    if (base64.startsWith(jpegPrefix)) {
      const data = base64.substring(jpegPrefix.length);
      const binaryString = atob(data);
      const byteArray = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        byteArray[i] = binaryString.charCodeAt(i);
      }
      return new Blob([byteArray], { type: 'image/jpeg' });
    } else {
      throw new Error('unsupported data type: ' + base64.split(',')[0]);
    }
  }
}
